import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CellContext } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flex, Heading } from 'theme-ui';

import IconMove from '@/assets/images/IconMove.svg?icon';
import IconTrash from '@/assets/images/IconTrash.svg?icon';
import { ActionMenuAddNewRow } from '@/components/Collection/ActionMenuAddNewRow';
import { ErrorLabel } from '@/components/Collection/Error';
import { MasterHomepageRowComponentActionsMenu } from '@/components/Collection/MasterHomepageRowComponentActionsMenu';
import { NEW_ITEMS_PINNING_PATH } from '@/components/Collection/NewItemsCollectionCallout';
import {
  formatDateRange,
  formatMarkets,
  formatMasterHomepageRowType,
  getHomepageComponentTypeForRow,
  getMasterHomepageBannerSide,
} from '@/components/Collection/utils';
import DataTable, { DataTableColumn } from '@/components/DataTable';
import {
  GetCollectionsPageQuery,
  GetMasterHomepageForEditQuery,
  GetWarehousesQuery,
  HomepageCollectionComponentType,
  MasterHomepageRowStructureType,
  useGetCollectionsPageQuery,
  useSaveMasterHomepageRowsMutation,
} from '@/graphql/types/generated';
import { useNewItemPinning } from '@/hooks/featureflipper';
import { useAppDispatch } from '@/hooks/store';
import { showToast, ToastAlertType } from '@/state/toast';

const NON_REMOVABLE_ROW_TYPES = new Set<string>([
  MasterHomepageRowStructureType.NewItemsCarousel,
  MasterHomepageRowStructureType.TrendingItemsCarousel,
]);

type OnDeleteCollection = () => Promise<void> | void;
type OnAddCollection = (
  rowId: string,
  componentType: HomepageCollectionComponentType,
) => void;
type OnEditCollection = (
  rowId: string,
  componentId: string,
  collectionId: string,
  componentType: HomepageCollectionComponentType,
) => void;
type Warehouse = GetWarehousesQuery['warehouses'][0];
type EditableRow = NonNullable<
  GetMasterHomepageForEditQuery['masterHomepageForEdit']
>['rows'][number];
type EditableRowComponent = EditableRow['components'][number];
type TableRowComponent = EditableRowComponent & {
  isPlaceholderRow?: true;
  isAddCollectionRow?: true;
  addCollectionRowId?: string;
  addCollectionComponentType?: HomepageCollectionComponentType;
};

function cloneRows(rows: EditableRow[]): EditableRow[] {
  return rows.map((row) => ({
    ...row,
    components: row.components.map((component) => ({ ...component })),
  }));
}

function getNextSortOrder(rows: EditableRow[]) {
  return rows.reduce((max, row) => Math.max(max, row.sortOrder), 0) + 1;
}

function getSpecialRowPlaceholder(structureType: string) {
  switch (structureType) {
    case MasterHomepageRowStructureType.NewItemsCarousel:
      return 'Algolia link to be generated';
    case MasterHomepageRowStructureType.TrendingItemsCarousel:
      return 'Managed by engineering';
    default:
      return null;
  }
}

// eslint-disable-next-line max-params
function getColumns(
  isEditing: boolean,
  totalWarehouses: number,
  onAddCollection: OnAddCollection,
  onEditCollection: OnEditCollection,
  warehousesById: Map<string, Warehouse>,
  onDeleteCollection: OnDeleteCollection,
  collectionNameById: Map<string, string>,
  structureType: MasterHomepageRowStructureType,
  isNewItemPinningEnabled: boolean,
): DataTableColumn<TableRowComponent>[] {
  // Behind the flag, the New Items carousel is a smart collection — its contents come from
  // system-defined rules, and this row just offers the ability to pin items within it. It has no
  // collection name, dates, or fixed markets, so those columns don't apply. Replace the row with a
  // single cell that links into the pinning editor.
  if (
    isNewItemPinningEnabled &&
    structureType === MasterHomepageRowStructureType.NewItemsCarousel
  ) {
    return [
      {
        id: 'new-items-pinning',
        header: '',
        cell: () => (
          <Link
            to={NEW_ITEMS_PINNING_PATH}
            sx={{
              fontWeight: 'bold',
              color: 'primaryDefault',
              textDecoration: 'none',
            }}
          >
            Manage pins →
          </Link>
        ),
      },
    ];
  }
  const columns: DataTableColumn<TableRowComponent>[] = [
    {
      header: 'Collection Name',
      id: 'collectionName',
      accessorFn: (component) =>
        component.isAddCollectionRow ? (
          <button
            disabled={isEditing}
            sx={{
              p: 0,
              border: 'none',
              bg: 'transparent',
              opacity: isEditing ? 0.5 : 1,
              fontWeight: 'bold',
              cursor: isEditing ? 'not-allowed' : 'pointer',
              color: isEditing ? 'neutralDark' : 'primaryDefault',
            }}
            onClick={(event) => {
              event.stopPropagation();
              onAddCollection(
                component.addCollectionRowId!,
                component.addCollectionComponentType!,
              );
            }}
          >
            + Add Collection
          </button>
        ) : (
          collectionNameById.get(component.collectionId) ||
          component.collectionId ||
          '(none)'
        ),
    },
    {
      header: 'Dates',
      id: 'dates',
      accessorFn: (component) =>
        !component.isPlaceholderRow && !component.isAddCollectionRow
          ? formatDateRange(component.startDate, component.endDate)
          : '',
    },
    {
      header: 'Markets',
      id: 'markets',
      accessorFn: (component) =>
        !component.isPlaceholderRow && !component.isAddCollectionRow
          ? formatMarkets(
              component.warehouseIds
                .map((warehouseId) => warehousesById.get(warehouseId))
                .filter((warehouse): warehouse is Warehouse => !!warehouse),
              totalWarehouses,
            )
          : '',
    },
    {
      id: 'edit',
      header: '',
      meta: { maxWidth: 34 },
      cell: ({ row }: CellContext<TableRowComponent, unknown>) =>
        !row.original.isPlaceholderRow &&
        !row.original.isAddCollectionRow &&
        row.original.collectionId &&
        row.original.catalogComponentId &&
        row.original.addCollectionComponentType &&
        row.original.addCollectionRowId ? (
          <MasterHomepageRowComponentActionsMenu
            isDisabled={isEditing}
            onDeleted={onDeleteCollection}
            rowId={row.original.addCollectionRowId}
            componentId={row.original.catalogComponentId}
            onEdit={() =>
              onEditCollection(
                row.original.addCollectionRowId!,
                row.original.catalogComponentId,
                row.original.collectionId,
                row.original.addCollectionComponentType!,
              )
            }
          />
        ) : null,
    },
  ];
  if (structureType === MasterHomepageRowStructureType.CollectionBanner) {
    columns.unshift({
      id: 'panel',
      header: 'Panel',
      meta: { maxWidth: 34 },
      cell: ({ row }: CellContext<TableRowComponent, unknown>) =>
        row.original.isAddCollectionRow ? null : (
          <div
            sx={{
              width: 36,
              height: 36,
              color: 'text',
              display: 'flex',
              border: '1px solid',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F9F9F7',
              borderColor: 'secondaryHighlight',
            }}
          >
            {getMasterHomepageBannerSide(row.original.sortOrder)}
          </div>
        ),
    });
  }
  return columns;
}

function SortableRow({
  row,
  homepage,
  onRemove,
  isEditing,
  warehousesById,
  totalWarehouses,
  onAddCollection,
  onEditCollection,
  collectionNameById,
  onDeleteCollection,
}: {
  row: EditableRow;
  isEditing: boolean;
  totalWarehouses: number;
  onAddCollection: OnAddCollection;
  onRemove: (rowId: string) => void;
  onEditCollection: OnEditCollection;
  onDeleteCollection: OnDeleteCollection;
  warehousesById: Map<string, Warehouse>;
  collectionNameById: Map<string, string>;
  homepage: NonNullable<GetMasterHomepageForEditQuery['masterHomepageForEdit']>;
}) {
  const {
    listeners,
    transform,
    transition,
    isDragging,
    attributes,
    setNodeRef,
    setActivatorNodeRef,
  } = useSortable({
    id: row.id,
    disabled: !isEditing,
  });
  const specialRowPlaceholder = getSpecialRowPlaceholder(row.structureType);
  const isNewItemPinningEnabled = useNewItemPinning();
  const homepageComponentType = getHomepageComponentTypeForRow(
    row.structureType,
  );
  const placeholderComponent: TableRowComponent = {
    id: `placeholder-${row.id}`,
    sortOrder: 0,
    catalogComponentId: '',
    collectionId: specialRowPlaceholder || '',
    componentType: row.structureType,
    startDate: homepage.startDate,
    endDate: homepage.endDate,
    warehouseIds: [],
    isPlaceholderRow: true,
  };
  const addCollectionRow: TableRowComponent = {
    sortOrder: 0,
    collectionId: '',
    warehouseIds: [],
    catalogComponentId: '',
    isAddCollectionRow: true,
    endDate: homepage.endDate,
    addCollectionRowId: row.id,
    startDate: homepage.startDate,
    id: `add-collection-${row.id}`,
    componentType: row.structureType,
    addCollectionComponentType: homepageComponentType ?? undefined,
  };
  const componentRows: TableRowComponent[] = row.components
    .slice()
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((component) => ({
      ...component,
      addCollectionRowId: row.id,
      addCollectionComponentType: homepageComponentType ?? undefined,
    }));
  const tableData: TableRowComponent[] = [
    ...(specialRowPlaceholder && row.components.length === 0
      ? [placeholderComponent]
      : componentRows),
    ...(homepageComponentType ? [addCollectionRow] : []),
  ];
  return (
    <Flex
      key={row.id}
      ref={setNodeRef}
      sx={{
        pb: 4,
        gap: 4,
        position: 'relative',
        boxShadow: isDragging ? '0 8px 24px rgba(0, 0, 0, 0.08)' : 'none',
        bg: isDragging ? 'white' : 'transparent',
      }}
      style={{ transition, transform: CSS.Transform.toString(transform) }}
    >
      {isEditing && (
        <div
          ref={setActivatorNodeRef}
          sx={{ pt: 1, cursor: 'grab' }}
          {...attributes}
          {...listeners}
        >
          <IconMove />
        </div>
      )}
      <div sx={{ width: '100%' }}>
        <Flex
          sx={{
            gap: 2,
            alignItems: 'center',
            '& button': {
              visibility: 'hidden',
            },
            '&:hover button': {
              visibility: 'visible',
            },
          }}
        >
          <Heading variant="headingSm">
            Row {row.sortOrder}
            {row.structureType ===
              MasterHomepageRowStructureType.CollectionBanner && 'A-B'}
            : {formatMasterHomepageRowType(row.structureType)}
          </Heading>
          {isEditing && !NON_REMOVABLE_ROW_TYPES.has(row.structureType) && (
            <button
              sx={{
                opacity: 0.5,
                border: 'none',
                bg: 'transparent',
                cursor: 'pointer',
                color: '#F4230B',
                '&:hover': {
                  opacity: 1,
                },
              }}
              onClick={() => onRemove(row.id)}
            >
              <IconTrash />
            </button>
          )}
        </Flex>
        <DataTable<TableRowComponent>
          data={tableData}
          columns={getColumns(
            isEditing,
            totalWarehouses,
            onAddCollection,
            onEditCollection,
            warehousesById,
            onDeleteCollection,
            collectionNameById,
            row.structureType,
            isNewItemPinningEnabled,
          )}
          containerStyles={{ marginTop: 0, paddingBottom: 0 }}
        />
      </div>
    </Flex>
  );
}

export function CatalogMasterHomepageRows({
  onSaved,
  homepage,
  isEditing,
  saveVersion,
  warehouses,
  totalWarehouses,
}: {
  isEditing: boolean;
  saveVersion: number;
  totalWarehouses: number;
  onSaved: () => Promise<void> | void;
  warehouses: GetWarehousesQuery['warehouses'];
  homepage: NonNullable<GetMasterHomepageForEditQuery['masterHomepageForEdit']>;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [saveError, setSaveError] = useState('');
  const [nextNewRowId, setNextNewRowId] = useState(1);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const [draftRows, setDraftRows] = useState<EditableRow[] | null>(null);
  const [{ data: collectionsPageData }] = useGetCollectionsPageQuery({
    requestPolicy: 'cache-and-network',
  });
  const [, saveMasterHomepageRows] = useSaveMasterHomepageRowsMutation();
  const lastHandledSaveVersionRef = useRef(0);
  const rows = draftRows ?? homepage.rows;
  const collectionNameById = useMemo(
    () =>
      new Map(
        (
          collectionsPageData?.collectionsPage
            ?.map((entry) => entry.collection)
            .filter(
              (
                collection,
              ): collection is GetCollectionsPageQuery['collectionsPage'][0]['collection'] =>
                !!collection?.id && !!collection?.name,
            ) ?? []
        ).map((collection) => [collection.id!, collection.name!]),
      ),
    [collectionsPageData],
  );
  const warehousesById = useMemo(
    () => new Map(warehouses.map((warehouse) => [warehouse.id, warehouse])),
    [warehouses],
  );

  const addCollection = (
    rowId: string,
    componentType: HomepageCollectionComponentType,
  ) => {
    const params = new URLSearchParams({
      rowId,
      componentType,
      masterHomepageId: homepage.id,
    });
    navigate(`/collections/add-to-homepage?${params.toString()}`);
  };

  const editCollection: OnEditCollection = (
    rowId,
    componentId,
    collectionId,
    componentType,
    // eslint-disable-next-line max-params
  ) => {
    const params = new URLSearchParams({
      rowId,
      componentId,
      collectionId,
      componentType,
      masterHomepageId: homepage.id,
    });
    navigate(`/collections/add-to-homepage?${params.toString()}`);
  };

  useEffect(() => {
    if (isEditing) {
      setDraftRows(cloneRows(homepage.rows));
      return;
    }
    setDraftRows(null);
    setSaveError('');
  }, [homepage.id, isEditing]);

  useEffect(() => {
    if (
      saveVersion === 0 ||
      saveVersion === lastHandledSaveVersionRef.current
    ) {
      return;
    }
    lastHandledSaveVersionRef.current = saveVersion;
    if (!isEditing) {
      return;
    }
    const saveRows = async () => {
      const result = await saveMasterHomepageRows({
        input: {
          id: homepage.id,
          rows: rows.map((row) => ({
            structureType: row.structureType,
            id: row.id.startsWith('new-row-') ? undefined : row.id,
          })),
        },
      });
      if (result.error) {
        setSaveError(result.error.message);
        return;
      }
      const errors =
        result.data?.saveMasterHomepageRows?.errors?.map(
          (error) => error.message,
        ) ?? [];
      if (errors.length > 0) {
        setSaveError(errors.join(', '));
        return;
      }
      setSaveError('');
      await onSaved();
      dispatch(
        showToast({
          duration: 4000,
          header: 'Rows updated',
          alertType: ToastAlertType.SUCCESS,
          content: 'The homepage rows have been updated.',
        }),
      );
    };
    saveRows();
  }, [
    dispatch,
    homepage.id,
    isEditing,
    onSaved,
    rows,
    saveMasterHomepageRows,
    saveVersion,
  ]);

  const addRow = (structureType: MasterHomepageRowStructureType) => {
    setDraftRows((currentRows) => {
      if (!currentRows) {
        return currentRows;
      }
      return [
        ...currentRows,
        {
          structureType,
          components: [],
          id: `new-row-${nextNewRowId}`,
          __typename: 'MasterHomepageRow',
          sortOrder: getNextSortOrder(currentRows),
        },
      ];
    });
    setNextNewRowId((value) => value + 1);
  };

  const removeRow = (rowId: string) => {
    setDraftRows((currentRows) =>
      currentRows ? currentRows.filter((row) => row.id !== rowId) : currentRows,
    );
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    setDraftRows((currentRows) => {
      if (!currentRows) {
        return currentRows;
      }
      const oldIndex = currentRows.findIndex((row) => row.id === active.id);
      const newIndex = currentRows.findIndex((row) => row.id === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return currentRows;
      }
      return arrayMove(currentRows, oldIndex, newIndex);
    });
  };

  return (
    <div>
      <ErrorLabel message={saveError} />
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={rows.map((row) => row.id)}
          strategy={verticalListSortingStrategy}
        >
          {rows.map((row) => (
            <SortableRow
              row={row}
              key={row.id}
              homepage={homepage}
              onRemove={removeRow}
              isEditing={isEditing}
              onDeleteCollection={onSaved}
              warehousesById={warehousesById}
              onAddCollection={addCollection}
              totalWarehouses={totalWarehouses}
              onEditCollection={editCollection}
              collectionNameById={collectionNameById}
            />
          ))}
        </SortableContext>
      </DndContext>
      {isEditing && <ActionMenuAddNewRow onSelect={addRow} />}
    </div>
  );
}
